import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Sidebar from "@/components/feature/Sidebar";
import SearchBar from "@/components/base/SearchBar";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { popularTags } from "@/lib/nav";
import { searchArticles } from "@/lib/content";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setTab("all");
  }, [query]);

  const allResults = useMemo(() => searchArticles(query), [query]);

  const counts: Record<string, number> = {
    all: allResults.length,
    laws: allResults.filter((item) => item.category === "laws").length,
    ethics: allResults.filter((item) => item.category === "ethics").length,
    cases: allResults.filter((item) => item.category === "cases").length,
  };

  const results = tab === "all" ? allResults : allResults.filter((item) => item.category === tab);

  const handleSearch = (keyword: string) => {
    setParams(keyword ? { q: keyword } : {});
    setTab("all");
  };

  const tabs = [
    { key: "all", label: "全部" },
    ...categories.map((category) => ({ key: category.type, label: category.name })),
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 py-8 md:px-8 lg:flex-row">
      <Sidebar />

      <div className="min-w-0 flex-1">
        <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <header className="mb-6">
          <h1 className="text-2xl font-bold text-foreground-950">全文检索</h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入关键词检索法律法规、伦理文章与案例分析，结果按相关度排序。
          </p>
          <div className="mt-5">
            <SearchBar size="lg" defaultValue={query} onSearch={handleSearch} />
          </div>
          {query && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词「<span className="font-semibold text-primary-700">{query}</span>」
              {allResults.length > 0 ? (
                <>
                  共找到 <span className="font-semibold text-primary-700">{allResults.length}</span> 条结果
                </>
              ) : (
                <>未匹配到内容</>
              )}
            </p>
          )}
        </header>

        {query ? (
          allResults.length > 0 ? (
            <>
              <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-background-200 pb-3">
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={[
                      "inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors",
                      tab === item.key
                        ? "bg-primary-600 font-medium text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200",
                    ].join(" ")}
                  >
                    {item.label}
                    <span
                      className={[
                        "rounded-full px-1.5 text-xs",
                        tab === item.key ? "bg-background-50/20" : "bg-background-50 text-foreground-500",
                      ].join(" ")}
                    >
                      {counts[item.key]}
                    </span>
                  </button>
                ))}
              </div>

              {results.length > 0 ? (
                <div className="flex flex-col gap-5">
                  {results.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="list" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
                  <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-file-search-line text-2xl" />
                  </span>
                  <h2 className="text-base font-bold text-foreground-950">该分类下暂无匹配结果</h2>
                  <p className="mt-2 text-sm text-foreground-600">试试切换到「全部」标签，或修改关键词。</p>
                  <button
                    type="button"
                    onClick={() => setTab("all")}
                    className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    查看全部结果
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <h2 className="text-lg font-bold text-foreground-950">没有找到与「{query}」相关的内容</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
                建议更换或缩短关键词，或从下方热门标签开始探索。我们推荐以下方向：
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {popularTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleSearch(tag)}
                    className="rounded-full bg-secondary-50 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-100"
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => handleSearch("")}
                className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-refresh-line" />
                清空并重新检索
              </button>
            </div>
          )
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="text-lg font-bold text-foreground-950">开始你的检索</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
              输入关键词，例如「禁婚亲属」「知情同意」「未成年人保护」，即可检索全站法规、伦理与案例内容。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {popularTags.slice(0, 5).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleSearch(tag)}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-background-200"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}